import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { fetchSearchDetail } from '../../services/searchService';
import { SEARCH_ROUTES } from './searchRoutes';
import { SEARCH_CATEGORIES, getSearchRouteState, itemId, navigateSearch, writeSearchSession } from './searchFlow';

export default function useSearchDetail(categoryId) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const route = location.pathname;
  const flow = getSearchRouteState(route, location.state);
  const id = Number(flow.id || itemId(flow.selectedRecord, categoryId)) || 0;
  const context = useMemo(() => {
    const state = getSearchRouteState(route, location.state);
    const result = {
      ...state, id, categoryId, currentPath: route,
      keyword: state.searchController ?? state.keyword ?? '',
      searchLogId: Number(state.searchLogId) || 0,
      stateId: Number(state.stateId) || 0,
      stateName: state.stateName || '',
      subjectId: Number(state.subjectId || state.selectedRecord?.subject_id) || 0,
      resultIndex: Number(state.resultIndex) || 1,
      totalResults: Number(state.totalResults) || 1,
    };
    writeSearchSession(`route:${route}`, result);
    return result;
  }, [route, location.state, id, categoryId]);
  const query = useQuery({
    queryKey: ['searchDetail', user?.id || user?.user_id, user?.preferred_lang || user?.content_language || 'EN', categoryId, id, context.keyword],
    queryFn: ({ signal }) => fetchSearchDetail({ categoryId, id, keyword: context.keyword }, { signal }),
    enabled: id > 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const goBack = () => {
    const target = context.searchBackTarget;
    if (target?.pathname) navigateSearch(navigate, target.pathname, target.state);
    else navigateSearch(navigate, SEARCH_CATEGORIES[categoryId]?.route || SEARCH_ROUTES.categories, context);
  };
  return { id, context, query, goBack, navigate, user };
}
